import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field", "toolbar", "output"]
  
  static values = {
    config: Object,
    height: { type: Number, default: 400 },
    plugins: { type: String, default: "link image code table lists advlist autolink autoresize wordcount searchreplace" },
    toolbar: { type: String, default: "undo redo | formatselect | bold italic | alignleft aligncenter alignright | bullist numlist outdent indent | link image | removeformat | help" }
  }
  
  // The TinyMCE instance
  editor = null

  connect() {
    console.log("Rich text editor controller connected")
    
    // Check if TinyMCE is available (either global or imported)
    this.ensureTinyMCEAvailable().then(() => {
      this.initializeEditor()
    }).catch((error) => {
      console.error("Failed to load TinyMCE:", error)
      this.showFallbackEditor()
    })
  }
  
  disconnect() {
    // Clean up by removing TinyMCE editor when the controller is disconnected
    if (this.editor) {
      this.editor.remove()
      this.editor = null
    }
  }
  
  // Ensure TinyMCE is loaded on the page
  ensureTinyMCEAvailable() {
    return new Promise((resolve, reject) => {
      // If TinyMCE is already loaded, resolve immediately
      if (window.tinymce) {
        resolve(window.tinymce)
        return
      }
      
      // Otherwise, try to load it from CDN
      const script = document.createElement('script')
      script.src = "https://cdn.tiny.cloud/1/no-api-key/tinymce/5/tinymce.min.js"
      script.referrerPolicy = "origin"
      
      script.onload = () => {
        console.log("TinyMCE loaded from CDN")
        resolve(window.tinymce)
      }
      
      script.onerror = () => {
        reject(new Error("Failed to load TinyMCE from CDN"))
      }
      
      document.head.appendChild(script)
    })
  }
  
  // Initialize the TinyMCE editor
  initializeEditor() {
    // Don't try to initialize if TinyMCE isn't available
    if (!window.tinymce) return
    
    // Don't initialize if field target is missing
    if (!this.hasFieldTarget) {
      console.error("Rich text editor is missing a field target")
      return
    }
    
    // Merge default config with any provided config
    const config = {
      target: this.fieldTarget,
      height: this.heightValue,
      plugins: this.pluginsValue,
      toolbar: this.toolbarValue,
      menubar: false,
      branding: false,
      statusbar: true,
      resize: true,
      autoresize_bottom_margin: 50,
      entity_encoding: "raw",
      valid_elements: "*[*]", // Allow all elements and attributes (default)
      paste_data_images: true,
      paste_as_text: false,
      convert_urls: false,
      relative_urls: false,
      setup: (editor) => {
        // Set up event handlers
        editor.on('change', () => this.handleEditorChange())
        
        // If using Turbo, ensure we save content before navigation
        document.addEventListener("turbo:before-visit", () => {
          this.handleEditorChange()
        })
      },
      ...this.configValue
    }
    
    // Initialize TinyMCE
    tinymce.init(config).then((editors) => {
      this.editor = editors[0]
      console.log("TinyMCE editor initialized")
    }).catch((error) => {
      console.error("TinyMCE initialization failed:", error)
      this.showFallbackEditor()
    })
  }
  
  // Handle changes in the editor (sync with hidden field)
  handleEditorChange() {
    if (!this.editor) return
    
    // If we have an output target, sync editor content to it
    if (this.hasOutputTarget) {
      this.outputTarget.value = this.editor.getContent()
    }
  }
  
  // Show a fallback editor if TinyMCE isn't available
  showFallbackEditor() {
    if (this.hasFieldTarget) {
      this.fieldTarget.style.display = "block"
      this.fieldTarget.style.height = `${this.heightValue}px`
      this.fieldTarget.style.width = "100%"
      
      // Basic toolbar buttons if we have a toolbar target
      if (this.hasToolbarTarget) {
        this.toolbarTarget.innerHTML = `
          <div class="rich-text-fallback-toolbar">
            <button type="button" onclick="document.execCommand('bold')">Bold</button>
            <button type="button" onclick="document.execCommand('italic')">Italic</button>
            <button type="button" onclick="document.execCommand('insertUnorderedList')">Bullet List</button>
            <button type="button" onclick="document.execCommand('insertOrderedList')">Number List</button>
            <button type="button" onclick="document.execCommand('createLink', false, prompt('Enter link URL'))">Link</button>
          </div>
        `
      }
      
      // Set up content editable
      this.fieldTarget.contentEditable = true
      this.fieldTarget.addEventListener("input", () => {
        if (this.hasOutputTarget) {
          this.outputTarget.value = this.fieldTarget.innerHTML
        }
      })
    }
  }
  
  // Public method to get the editor content
  getContent() {
    if (this.editor) {
      return this.editor.getContent()
    } else if (this.hasFieldTarget && this.fieldTarget.contentEditable) {
      return this.fieldTarget.innerHTML
    }
    return ""
  }
  
  // Public method to set the editor content
  setContent(html) {
    if (this.editor) {
      this.editor.setContent(html)
    } else if (this.hasFieldTarget && this.fieldTarget.contentEditable) {
      this.fieldTarget.innerHTML = html
    }
  }
}